/**
 * SegmentedButton class recipe — composes size + color (appearance × intent).
 */

import { cn } from '@/utils/cn';
import {
  getSegmentedActiveClass,
  getSegmentedActiveInk,
  getSegmentedActiveSurface,
  getSegmentedFocusClass,
  getSegmentedInactiveClass,
  getSegmentedTrackSurface,
} from './SegmentedButton.color';
import {
  SEGMENTED_ICON,
  SEGMENTED_ROUNDED_SEGMENT,
  SEGMENTED_ROUNDED_TRACK,
  SEGMENTED_SEGMENT,
  SEGMENTED_SEGMENT_EQUAL,
  SEGMENTED_TRACK_GAP,
  SEGMENTED_TRACK_PAD,
  SEGMENTED_TYPE,
} from './SegmentedButton.size';
import type {
  SegmentedButtonAppearance,
  SegmentedButtonIntent,
  SegmentedButtonRounded,
  SegmentedButtonSize,
} from './SegmentedButton.types';

const FOCUS_BASE = 'focus-visible:outline-none focus-visible:ring-2';

export function getSegmentedTrackClassName(
  size: SegmentedButtonSize,
  rounded: SegmentedButtonRounded,
  equalWidth: boolean,
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
  className?: string,
  hasTrack = true,
): string {
  return cn(
    'relative inline-flex items-center',
    equalWidth && 'w-full',
    SEGMENTED_TRACK_GAP[size],
    hasTrack && SEGMENTED_TRACK_PAD[size],
    hasTrack && SEGMENTED_ROUNDED_TRACK[rounded],
    hasTrack && getSegmentedTrackSurface(appearance, intent),
    className,
  );
}

export function getSegmentedOptionClassName(
  size: SegmentedButtonSize,
  rounded: SegmentedButtonRounded,
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
  equalWidth: boolean,
  isActive: boolean,
  disabled: boolean,
  hasSlideIndicator: boolean,
): string {
  return cn(
    'relative z-10 inline-flex items-center justify-center transition-colors',
    equalWidth ? 'flex-1' : 'shrink-0',
    equalWidth ? SEGMENTED_SEGMENT_EQUAL[size] : SEGMENTED_SEGMENT[size],
    SEGMENTED_TYPE[size],
    SEGMENTED_ROUNDED_SEGMENT[rounded],
    FOCUS_BASE,
    getSegmentedFocusClass(intent),
    disabled && 'opacity-50 cursor-not-allowed',
    // When sliding, ink only — surface comes from the motion indicator.
    hasSlideIndicator
      ? isActive
        ? getSegmentedActiveInk(appearance, intent)
        : getSegmentedInactiveClass(intent)
      : isActive
        ? getSegmentedActiveClass(appearance, intent)
        : getSegmentedInactiveClass(intent),
  );
}

export function getSegmentedIconClassName(size: SegmentedButtonSize): string {
  return cn('inline-flex shrink-0 items-center [&_svg]:text-current', SEGMENTED_ICON[size]);
}

export function getSegmentedSlideIndicatorClassName(
  rounded: SegmentedButtonRounded,
  appearance: SegmentedButtonAppearance,
  intent: SegmentedButtonIntent,
): string {
  return cn(
    'absolute z-0 pointer-events-none',
    SEGMENTED_ROUNDED_SEGMENT[rounded],
    getSegmentedActiveSurface(appearance, intent),
  );
}
